import {
  ColorModeWithSystem,
  useColorMode,
  useColorModePreference,
} from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";

export type StoredColorMode = NonNullable<ColorModeWithSystem>;

export default function useColorModeStorage() {
  const { colorMode: chakraColorMode, setColorMode: setChakraColorMode } =
    useColorMode();

  const systemColorMode = useColorModePreference();

  // we implement our own storage of user preference because
  // chakra doesn't support "system" color mode out of the box
  const [storedColorMode, setStoredColorMode] =
    useLocalStorage<StoredColorMode>("color-mode", "system");

  // update the stored color mode and the chakra color mode, but try to avoid unnecessary re-updates
  const update = useCallback(
    (newValue: StoredColorMode) => {
      setStoredColorMode(newValue);
      if (newValue === "system") {
        if (systemColorMode && systemColorMode !== chakraColorMode) {
          setChakraColorMode(systemColorMode);
        }
      } else {
        setChakraColorMode(newValue);
      }
    },
    [chakraColorMode, setChakraColorMode, setStoredColorMode, systemColorMode],
  );

  // listen to system color mode changes and only update the color mode if
  // 1. "system" is chosen by user and
  // 2. the system color mode is different from the current color mode (to avoid multiple updates)
  useEffect(() => {
    if (
      systemColorMode &&
      storedColorMode === "system" &&
      systemColorMode !== chakraColorMode
    ) {
      update("system");
    }
  }, [systemColorMode, update, storedColorMode, chakraColorMode]);

  const toggle = useCallback(() => {
    switch (storedColorMode) {
      case "dark":
        update("light");
        break;
      case "light":
        update("system");
        break;
      case "system":
        update("dark");
        break;
      default: {
        throw new Error(`Invalid color mode: ${storedColorMode}`);
      }
    }
  }, [storedColorMode, update]);

  return [storedColorMode, toggle] as const;
}
